import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ChevronDown, Trash2 } from "@digital-twins/design-system/icons";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface QuestionLogicSectionProps {
  icon: ReactNode;
  summary: string;
  tone?: "default" | "destructive";
  isExpanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  testIdPrefix: string;
  questionId: string;
  disabled?: boolean;
  children: ReactNode;
}

export function QuestionLogicSection(props: QuestionLogicSectionProps) {
  const {
    icon,
    summary,
    tone = "default",
    isExpanded,
    onToggle,
    onRemove,
    testIdPrefix,
    questionId,
    disabled,
    children,
  } = props;

  const { t } = useTranslation("app");

  const bodyId = `${testIdPrefix}-section-body-${questionId}`;
  const isDestructive = tone === "destructive";

  return (
    <TooltipProvider>
      <div
        className="mb-3 flex flex-col gap-2 rounded-[8px] border border-dashed border-border bg-muted/30 px-3 py-2.5 dark:bg-white/[0.03]"
        data-testid={`${testIdPrefix}-section-${questionId}`}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={cn(
              "flex min-w-0 flex-1 items-center gap-1.5 text-left text-[12.5px]",
              isDestructive ? "text-destructive" : "text-muted-foreground",
            )}
            onClick={onToggle}
            aria-expanded={isExpanded}
            aria-controls={bodyId}
            data-testid={`${testIdPrefix}-section-summary-${questionId}`}
          >
            <span
              className={cn(
                "shrink-0",
                isDestructive ? "text-destructive" : "text-muted-foreground",
              )}
            >
              {icon}
            </span>
            <span className="truncate">{summary}</span>
          </button>
          <div className="flex shrink-0 items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 rounded-[6px]"
                  onClick={onToggle}
                  aria-expanded={isExpanded}
                  aria-controls={bodyId}
                  aria-label={
                    isExpanded
                      ? t("questionnaire.logic.collapse")
                      : t("questionnaire.logic.expand")
                  }
                  data-testid={`${testIdPrefix}-section-toggle-${questionId}`}
                >
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform",
                      isExpanded ? "rotate-180" : "rotate-0",
                    )}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isExpanded
                  ? t("questionnaire.logic.collapse")
                  : t("questionnaire.logic.expand")}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 rounded-[6px]"
                  onClick={onRemove}
                  disabled={disabled}
                  aria-label={t("questionnaire.logic.remove")}
                  data-testid={`${testIdPrefix}-section-remove-${questionId}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("questionnaire.logic.remove")}</TooltipContent>
            </Tooltip>
          </div>
        </div>
        {isExpanded ? <div id={bodyId}>{children}</div> : null}
      </div>
    </TooltipProvider>
  );
}
